import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "antd";
import {
  Search,
  Grid3X3,
  List,
  Star,
  MessageCircle,
  Inbox,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Button, Input } from "@/design-system/components";
import { PageHeading } from "@/design-system/components/heading/heading";
import { colors } from "@/design-system/theme/colors";
import { useTemplates } from "../api";
import { useWidgetFilters } from "../store";
import type { WidgetTemplate } from "../types";

export function LibraryPage() {
  const navigate = useNavigate();
  const { filters, viewMode, setSearchQuery, setViewMode, resetFilters } =
    useWidgetFilters();
  const { templates, isLoading, refetch } = useTemplates();

  // Filter templates by search only
  const filteredTemplates = useMemo(() => {
    if (!filters.search) return templates;
    const query = filters.search.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query)
    );
  }, [templates, filters.search]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [setSearchQuery]
  );

  const handleCustomize = useCallback(
    (template: WidgetTemplate) => {
      navigate(`/customize/${template.id}`);
    },
    [navigate]
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    resetFilters();
  }, [setSearchQuery, resetFilters]);

  return (
    <div className="h-full" style={{ backgroundColor: colors.surfaceLightest }}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <PageHeading
          title="Widget Templates"
          subheading="Choose a template and customize it to match your brand"
          divider={false}
          noMargin
          className="mb-8"
        />

        {/* Search & View Toggle - Clean row without bg/border */}
        <div className="flex items-center gap-6 mb-8">
          {/* Search Input */}
          <div className="flex-1 max-w-lg">
            <Input
              placeholder="Search templates..."
              size="medium"
              prefix={<Search size={18} color={colors.textMuted} />}
              value={filters.search}
              onChange={handleSearchChange}
              allowClear
            />
          </div>

          {/* Results Count */}
          <div className="hidden sm:flex items-center gap-2">
            <span
              className="text-sm font-medium"
              style={{ color: colors.textMuted }}
            >
              {filteredTemplates.length}{" "}
              {filteredTemplates.length === 1 ? "template" : "templates"}
            </span>
          </div>

          {/* View Toggle */}
          <div
            className="flex items-center gap-1 p-1 rounded-lg"
            style={{ backgroundColor: colors.surfaceLighter }}
          >
            <Button
              variant={viewMode === "grid" ? "primary" : "ghost"}
              size="small"
              leftIcon={<Grid3X3 size={18} />}
              onClick={() => setViewMode("grid")}
              style={{
                backgroundColor:
                  viewMode === "grid" ? colors.primary : "transparent",
                color: viewMode === "grid" ? colors.white : colors.textMuted,
              }}
            />
            <Button
              variant={viewMode === "list" ? "primary" : "ghost"}
              size="small"
              leftIcon={<List size={18} />}
              onClick={() => setViewMode("list")}
              style={{
                backgroundColor:
                  viewMode === "list" ? colors.primary : "transparent",
                color: viewMode === "list" ? colors.white : colors.textMuted,
              }}
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card
                key={i}
                loading
                style={{
                  borderRadius: 12,
                  border: `1px solid ${colors.surfaceSubtle}`,
                }}
              />
            ))}
          </div>
        )}

        {/* Empty State - No Templates Available */}
        {!isLoading && templates.length === 0 && (
          <Card
            style={{
              borderRadius: 16,
              border: `1px solid ${colors.surfaceSubtle}`,
              textAlign: "center",
              padding: "60px 24px",
            }}
          >
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: colors.surfaceLighter }}
            >
              <Inbox size={40} color={colors.textMuted} />
            </div>
            <h3
              className="text-xl font-semibold mb-3"
              style={{ color: colors.textInverse }}
            >
              No Templates Available
            </h3>
            <p
              className="text-base mb-8 max-w-md mx-auto"
              style={{ color: colors.textMuted }}
            >
              Templates are being prepared. Please check back soon or refresh to
              see the latest updates.
            </p>
            <Button
              variant="secondary"
              size="medium"
              leftIcon={<RefreshCw size={18} />}
              onClick={() => refetch?.()}
            >
              Refresh
            </Button>
          </Card>
        )}

        {/* Empty State - No Search Results */}
        {!isLoading &&
          templates.length > 0 &&
          filteredTemplates.length === 0 && (
            <Card
              style={{
                borderRadius: 16,
                border: `1px solid ${colors.surfaceSubtle}`,
                textAlign: "center",
                padding: "60px 24px",
              }}
            >
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: colors.surfaceLighter }}
              >
                <Search size={40} color={colors.textMuted} />
              </div>
              <h3
                className="text-xl font-semibold mb-3"
                style={{ color: colors.textInverse }}
              >
                No Results Found
              </h3>
              <p
                className="text-base mb-8 max-w-md mx-auto"
                style={{ color: colors.textMuted }}
              >
                We couldn't find any templates matching "{filters.search}". Try
                a different search term.
              </p>
              <Button
                variant="primary"
                size="medium"
                onClick={handleClearSearch}
              >
                Clear Search
              </Button>
            </Card>
          )}

        {/* Template Grid */}
        {!isLoading && filteredTemplates.length > 0 && (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                : "flex flex-col gap-4"
            }
          >
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                viewMode={viewMode}
                onCustomize={() => handleCustomize(template)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface TemplateCardProps {
  template: WidgetTemplate;
  viewMode: "grid" | "list";
  onCustomize: () => void;
}

function TemplateCard({ template, viewMode, onCustomize }: TemplateCardProps) {
  const primaryColor = template.themeStyles?.primaryColor || "#25D366";
  const brandName = template.brandConfig?.brandName || "Notifyy";

  // Mini Widget Preview - Larger size
  const WidgetPreview = () => (
    <div className="w-full h-full flex items-center justify-center p-4">
      <div
        className="w-36 rounded-xl overflow-hidden"
        style={{
          backgroundColor: colors.white,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        }}
      >
        {/* Header */}
        <div
          className="px-3 py-2 flex items-center gap-2"
          style={{ backgroundColor: primaryColor }}
        >
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ backgroundColor: "rgba(255,255,255,0.25)", color: "#fff" }}
          >
            {brandName.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-white truncate">
              {brandName}
            </div>
            <div className="text-[10px] text-white/70">Online</div>
          </div>
        </div>
        {/* Message */}
        <div className="px-3 py-2" style={{ backgroundColor: "#ECE5DD" }}>
          <div
            className="bg-white px-2 py-1.5 rounded-lg text-[10px] leading-tight"
            style={{ color: colors.textInverse }}
          >
            Hi! How can I help you today?
          </div>
        </div>
        {/* CTA */}
        <div className="px-3 py-2" style={{ backgroundColor: colors.white }}>
          <div
            className="text-center py-1.5 rounded-lg text-[10px] font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            Start Chat
          </div>
        </div>
      </div>
    </div>
  );

  if (viewMode === "list") {
    return (
      <Card
        hoverable
        onClick={onCustomize}
        style={{
          borderRadius: 12,
          border: `1px solid ${colors.surfaceSubtle}`,
        }}
        styles={{
          body: { padding: "16px 20px" },
        }}
      >
        <div className="flex items-center gap-5">
          {/* Preview Thumbnail */}
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <MessageCircle size={28} color={primaryColor} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <span
                className="text-base font-semibold truncate"
                style={{ color: colors.textInverse }}
              >
                {template.name}
              </span>
              {template.isPopular && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1"
                  style={{
                    backgroundColor: colors.primaryExtraLight,
                    color: colors.primary,
                  }}
                >
                  <Sparkles size={12} />
                  Popular
                </span>
              )}
            </div>
            <p className="text-sm truncate" style={{ color: colors.textMuted }}>
              {template.description}
            </p>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1.5 shrink-0">
            <Star size={16} color={colors.warning} fill={colors.warning} />
            <span
              className="text-base font-medium"
              style={{ color: colors.textDefault }}
            >
              {template.rating}
            </span>
          </div>

          {/* Action */}
          <Button
            variant="primary"
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              onCustomize();
            }}
          >
            Customize
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      hoverable
      onClick={onCustomize}
      cover={
        <div
          className="h-48 relative overflow-hidden"
          style={{
            background: `linear-gradient(160deg, ${primaryColor}20 0%, ${primaryColor}08 100%)`,
          }}
        >
          <WidgetPreview />

          {/* Badge */}
          {template.isPopular && (
            <span
              className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold shadow-md flex items-center gap-1"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              <Sparkles size={12} />
              Popular
            </span>
          )}

          {/* Hover Overlay */}
          <div
            className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-all duration-300 backdrop-blur-[2px]"
            style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
          >
            <Button
              variant="primary"
              size="medium"
              onClick={(e) => {
                e.stopPropagation();
                onCustomize();
              }}
            >
              Customize Template
            </Button>
          </div>
        </div>
      }
      style={{
        borderRadius: 12,
        border: `1px solid ${colors.surfaceSubtle}`,
        overflow: "hidden",
      }}
      styles={{
        body: { padding: "16px 20px" },
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-base font-semibold truncate"
          style={{ color: colors.textInverse }}
        >
          {template.name}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          <Star size={14} color={colors.warning} fill={colors.warning} />
          <span
            className="text-sm font-medium"
            style={{ color: colors.textDefault }}
          >
            {template.rating}
          </span>
        </div>
      </div>
      <p className="text-sm" style={{ color: colors.textMuted }}>
        {template.description}
      </p>
    </Card>
  );
}

export default LibraryPage;
